import React, { useState, useRef, useMemo } from 'react';
import * as styles from './styles.module.css';
import Modal from '../Modal';

import {
  MediaControlBar,
  MediaController,
  MediaMuteButton,
  MediaVolumeRange,
  MediaPlayButton,
  MediaTimeDisplay,
  MediaTimeRange,
} from 'media-chrome/react';
import { TbPlayerTrackNextFilled, TbPlayerTrackPrevFilled } from 'react-icons/tb';
import { RiPlayListFill } from 'react-icons/ri';
import { FaPlay } from 'react-icons/fa6';
import { useIsMobile } from '@src/hooks/useBreakpoints';

type AudioPlayerProps = {
  audioFiles: {
    id: string;
    fileUrl: string;
    title: string;
    genres: {
      label: string;
    }[];
  }[];
  genres: {
    label: string;
  }[];
};

const AudioPlayer = ({ audioFiles, genres }: AudioPlayerProps) => {
  const [currentAudioFile, setCurrentAudioFile] = useState(audioFiles[0]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

  // Filter audio files based on selected genres
  const filteredAudioFiles = useMemo(() => {
    if (selectedGenres.length === 0) {
      return audioFiles;
    }
    const filtered = audioFiles.filter((audio) =>
      audio.genres.some((genre) => selectedGenres.includes(genre.label)),
    );

    // Deduplicate based on id to prevent repeated items
    const seen = new Set();
    return filtered.filter((audio) => {
      if (seen.has(audio.id)) {
        return false;
      }
      seen.add(audio.id);
      return true;
    });
  }, [audioFiles, selectedGenres]);

  const handleAudioEnd = async () => {
    const currentIndex = audioFiles.indexOf(currentAudioFile);
    const nextIndex = (currentIndex + 1) % audioFiles.length;
    setCurrentAudioFile(audioFiles[nextIndex]);
    await handleAudioPlay();
  };

  const handleAudioPlay = async () => {
    if (audioRef.current) {
      audioRef.current.load();

      await new Promise((resolve) => {
        audioRef.current!.addEventListener('canplay', resolve, { once: true });
      });

      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleNext = async () => {
    const currentIndex = audioFiles.indexOf(currentAudioFile);
    const nextIndex = (currentIndex + 1) % audioFiles.length;
    setCurrentAudioFile(audioFiles[nextIndex]);
    await handleAudioPlay();
  };

  const handlePrevious = async () => {
    const currentIndex = audioFiles.indexOf(currentAudioFile);
    const prevIndex = (currentIndex - 1 + audioFiles.length) % audioFiles.length;
    setCurrentAudioFile(audioFiles[prevIndex]);
    await handleAudioPlay();
  };

  const handlePlaylistItemClick = async (audioFile: (typeof audioFiles)[0]) => {
    setCurrentAudioFile(audioFile);
    setShowPlaylist(false);
    await handleAudioPlay();
  };

  const handleGenreToggle = (genreLabel: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genreLabel) ? prev.filter((g) => g !== genreLabel) : [...prev, genreLabel],
    );
  };

  const clearFilters = () => {
    setSelectedGenres([]);
  };

  const isMobile = useIsMobile();

  return (
    <div className={styles.audioPlayerWrapper}>
      <MediaController audio>
        <audio
          ref={audioRef}
          slot="media"
          src={`https://mymathscloud.ams3.digitaloceanspaces.com/Audio/${currentAudioFile.fileUrl}`}
          onEnded={handleAudioEnd}
        />
        <MediaControlBar>
          <MediaPlayButton onClick={() => setIsPlaying(!isPlaying)} />
          <MediaTimeDisplay />
          {!isMobile && <MediaTimeRange />}
          <MediaMuteButton />
          <button onClick={handlePrevious}>
            <TbPlayerTrackPrevFilled />
          </button>
          <button onClick={handleNext}>
            <TbPlayerTrackNextFilled />
          </button>
          <button onClick={() => setShowPlaylist(true)}>
            <RiPlayListFill />
          </button>
          <MediaVolumeRange />
        </MediaControlBar>
      </MediaController>
      {isPlaying && (
        <div className={styles.controlBar}>
          <span>Playing {currentAudioFile.title}</span>
        </div>
      )}

      {showPlaylist && (
        <Modal title="Relaxation Song Playlist" onClose={() => setShowPlaylist(false)}>
          <div className={styles.playlistContainer}>
            {/* Genre Filters */}
            <div className={styles.genreFilters}>
              <div className={styles.genreFiltersHeader}>
                <h4>Filter by Genre</h4>
                {selectedGenres.length > 0 && (
                  <button onClick={clearFilters} className={styles.clearFiltersBtn}>
                    Clear Filters
                  </button>
                )}
              </div>
              <div className={styles.genreButtons}>
                {genres.map((genre) => (
                  <button
                    key={genre.label}
                    onClick={() => handleGenreToggle(genre.label)}
                    className={`${styles.genreButton} ${
                      selectedGenres.includes(genre.label) ? styles.active : ''
                    }`}
                  >
                    {genre.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Playlist Items */}
            <div className={styles.playlistItems}>
              {filteredAudioFiles.length === 0 ? (
                <div className={styles.noResults}>No songs match the selected genres</div>
              ) : (
                filteredAudioFiles.map((audio, index) => {
                  const isCurrentTrack = audio === currentAudioFile;

                  return (
                    <div
                      key={`${audio.id}-${index}`}
                      className={`${styles.playlistItem} ${
                        isCurrentTrack ? styles.currentTrack : ''
                      }`}
                      onClick={() => handlePlaylistItemClick(audio)}
                    >
                      <div className={styles.trackInfo}>
                        <span className={styles.trackNumber}>{index + 1}</span>
                        <div className={styles.trackDetails}>
                          <div className={styles.trackTitleRow}>
                            <span className={styles.trackTitle}>{audio.title}</span>
                            <div className={styles.trackGenres}>
                              {audio.genres.map((genre, idx) => (
                                <span key={idx} className={styles.genreTag}>
                                  {genre.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      {isCurrentTrack && isPlaying && (
                        <span className={styles.playingIndicator}>
                          <FaPlay />
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AudioPlayer;
